import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { LocaleContext, Spinner, useToastNotification } from 'libs.nucleus';
import { ReportingCard } from 'libs.react';
import { ApiClientService, useApiClient } from 'libs.react_internal';
import { ChartContent } from './report_chart_content.component';
import { ChartHeader } from './report_chart_header.component';
import { DataReportsContext } from '../../../contexts/data_reports/data_reports.context';
import { ReportAnalyticsEvents } from '../../constants/report_analytics_events.constants';
import { usePaginatedCubeQuery } from '../../hooks/use_paginated_cube_query';
import { useServerSideCubeQuery } from '../../hooks/use_server_side_cube_query';
import { exportJsonToCsv, getExportTimestamp, getQueryParams, getTransformedExportData, sanitizeFileName, } from '../../utils/data_reports.utils';
import { buildDefaultSortOrder, normalizeDefaultSort, withSortTiebreakers } from '../../utils/order.utils';
import { CompositeCard } from '../composite_card';
export const ChartCard = ({ chart, chartQueryFilters, chartPresetFilters, setChartFilterModal, filterParams, studyId, reportId, isPublic, onNonTableResultSetsChange, onChartLoadStatus, onLastRefreshTime, logReportEvent, forceDisablePreAggs = false, refreshNonce = 0, interactionsDisabled = false, }) => {
    // All hooks must be called before any conditional returns
    const libraryClient = useApiClient(ApiClientService.LIBRARY);
    const toast = useToastNotification();
    const locale = useContext(LocaleContext).toString();
    // `showFullTimestamp` reflects whether this study report was opened via the new
    // flow (has `rid`). Applied to on-screen TableChart + CSV export so both match (SSP-4116).
    // `applyValueFormatting` is true only for new-flow reports (`rid`); legacy single-chart CSV exports
    // stay raw (no percentages / abbreviations), matching the removed formatted-export option (SSP-4105).
    const { libRequestsParams, showFullTimestamp, applyValueFormatting } = useContext(DataReportsContext);
    const [sortingConfig, setSortingConfig] = useState({});
    const [lastRefreshed, setLastRefreshed] = useState('');
    const [isLoadingResultSet, setIsLoadingResultSet] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [shouldFetchForExport, setShouldFetchForExport] = useState(false);
    // Analytics: chart load timing
    const chartLoadStartRef = useRef(performance.now());
    const chartLoadLoggedRef = useRef(false);
    const DEFAULT_TABLE_ROWS_PER_PAGE = 10;
    // Analytics: fire chart load started on mount
    useEffect(() => {
        if (chart.type === 'composite') {
            return;
        }
        chartLoadStartRef.current = performance.now();
        chartLoadLoggedRef.current = false;
        onChartLoadStatus?.(chart.id, 'loading');
        logReportEvent?.(ReportAnalyticsEvents.CHART_LOAD_STARTED, {
            chart_id: chart.id,
            chart_type: chart.type,
            chart_title: chart.title,
        });
    }, [chart.id]);
    const getChartData = async () => {
        const params = chart.libraryResourceId
            ? { id: chart.libraryResourceId, ...(isPublic && { public: true }), ...libRequestsParams }
            : { key: chart.id };
        // check entity lib first, and then public
        const { data: response } = await libraryClient.get('/v1/reporting_chart', {
            params,
        });
        if (!chart.libraryResourceId && response.data.length === 0) {
            const { data: publicResponse } = await libraryClient.get('/v1/reporting_chart', {
                params: { ...params, public: true },
            });
            return publicResponse.data[0]?.data;
        }
        return response.data[0]?.data;
    };
    const { data: chartData, isLoading: isLoadingChartData } = useQuery({
        queryKey: ['data_reports', studyId, 'reports_list', reportId, 'charts', chart.id],
        queryFn: getChartData,
        enabled: chart.type !== 'composite', // Skip API call for composite charts
    });
    // Prefer fetched chart config over dashboard chart ref (study imports may omit type).
    const isTableChart = (chartData?.type ?? chart.type) === 'table';
    const configuredTablePageSize = chartData?.defaultRowsPerPage ?? DEFAULT_TABLE_ROWS_PER_PAGE;
    // Builder-configured default sort (SSP-3919). The persisted query.order can
    // be stale (e.g. the study renders a pinned chart version saved before the
    // sort was added), so — like the Library views, which rebuild the order from
    // defaultSort on every run — it must be re-derived here rather than trusted
    // from the saved query.
    const configuredDefaultSort = useMemo(() => normalizeDefaultSort(chartData?.defaultSort), [chartData?.defaultSort]);
    const [tablePageIndex, setTablePageIndex] = useState(0);
    const [userTablePageSize, setUserTablePageSize] = useState(null);
    useEffect(() => {
        setTablePageIndex(0);
        setUserTablePageSize(null);
    }, [chart.id, chartData?.defaultRowsPerPage]);
    const paginationState = useMemo(() => ({
        pageIndex: tablePageIndex,
        pageSize: userTablePageSize ?? configuredTablePageSize,
    }), [tablePageIndex, userTablePageSize, configuredTablePageSize]);
    // Transform query based on sorting configuration. When the user sorts, append
    // the chart's other columns as tiebreakers so cube LIMIT/OFFSET stays
    // deterministic across pages when the sorted column has ties (SSP-3889).
    // Until the user sorts, seed the order from the builder-configured defaultSort
    // so the study view matches the Library (SSP-4500).
    const transformedQuery = useMemo(() => {
        if (!chartData) {
            return undefined;
        }
        const tiebreakerKeys = [...(chartData.query?.dimensions || []), ...(chartData.query?.measures || [])];
        const defaultSortOrder = configuredDefaultSort && buildDefaultSortOrder(configuredDefaultSort, tiebreakerKeys);
        return {
            ...chartData.query,
            ...{ renewQuery: true, total: true },
            ...(Object.keys(sortingConfig).length > 0
                ? { order: withSortTiebreakers(sortingConfig, tiebreakerKeys) }
                : defaultSortOrder && { order: defaultSortOrder }),
        };
    }, [chartData, sortingConfig, configuredDefaultSort]);
    const queryParams = useMemo(() => getQueryParams(transformedQuery, filterParams, chartQueryFilters[chart.id], chartPresetFilters[chart.id]), [transformedQuery, filterParams, chartQueryFilters[chart.id], chartPresetFilters[chart.id], chart.id]);
    // Analytics: callback for tracking query executions
    const handleQueryExecuted = useCallback(() => {
        logReportEvent?.(ReportAnalyticsEvents.QUERY_EXECUTED, {
            chart_id: chart.id,
            chart_type: chart.type,
            chart_title: chartData?.title ?? chart.title,
        });
    }, [logReportEvent, chart.id, chart.type, chartData?.title, chart.title]);
    // Use server-side pagination for table charts, full pagination for other chart types
    // Both hooks must be called unconditionally (React rules of hooks)
    // For table charts, we enable paginatedResult when exporting to fetch all data
    const paginatedResult = usePaginatedCubeQuery(queryParams, {
        skip: !chartData || (!isLoadingResultSet && !isTableChart) || (isTableChart && !shouldFetchForExport),
        disablePreAggs: chartData?.disablePreAggs,
        forceFresh: forceDisablePreAggs,
        refreshNonce,
        onQueryExecuted: handleQueryExecuted,
    });
    const serverSideResult = useServerSideCubeQuery(queryParams, {
        skip: !chartData || !isTableChart,
        disablePreAggs: chartData?.disablePreAggs,
        forceFresh: forceDisablePreAggs,
        refreshNonce,
        pagination: paginationState,
        onQueryExecuted: handleQueryExecuted,
    });
    // Select the appropriate result based on chart type. Memoized so table charts get a stable
    // array reference across renders instead of a fresh `[serverSideResult.resultSet]` literal each
    // time — several effects below key off `resultSets`, and an ever-new reference made them refire
    // on every render regardless of whether the underlying result actually changed.
    const resultSets = useMemo(() => isTableChart ? (serverSideResult.resultSet ? [serverSideResult.resultSet] : null) : paginatedResult.resultSets, [isTableChart, serverSideResult.resultSet, paginatedResult.resultSets]);
    const isLoading = isTableChart ? serverSideResult.isLoading : paginatedResult.isLoading;
    const error = isTableChart ? serverSideResult.error : paginatedResult.error;
    const totalRows = serverSideResult.total;
    // Reset pagination when filters/sorting change (only for table charts)
    useEffect(() => {
        if (isTableChart) {
            setTablePageIndex(0);
        }
    }, [queryParams, isTableChart]);
    useEffect(() => {
        if (!isTableChart) {
            setIsLoadingResultSet(true);
        }
    }, [queryParams, isTableChart, refreshNonce]);
    // On manual dashboard refresh, re-arm load tracking so the chart reports a fresh
    // loading -> loaded/failed cycle. This both re-enables non-table fetches (via the
    // reset above) and lets the dashboard detect when the refresh has fully settled.
    useEffect(() => {
        if (!refreshNonce || chart.type === 'composite') {
            return;
        }
        chartLoadStartRef.current = performance.now();
        chartLoadLoggedRef.current = false;
        onChartLoadStatus?.(chart.id, 'loading');
    }, [refreshNonce]);
    useEffect(() => {
        if (resultSets && resultSets[0]) {
            const loadResponse = resultSets[0]?.loadResponse?.results?.[0];
            const refreshTime = loadResponse?.lastRefreshTime ?? '';
            setLastRefreshed(refreshTime);
            if (refreshTime) {
                onLastRefreshTime?.(chart.id, refreshTime);
            }
        }
    }, [resultSets]);
    useEffect(() => {
        if (!isTableChart && (resultSets || error)) {
            setIsLoadingResultSet(false);
        }
    }, [resultSets, error, isTableChart]);
    // Analytics: fire chart load completed or failed when loading settles
    useEffect(() => {
        if (chart.type === 'composite' || chartLoadLoggedRef.current) {
            return;
        }
        // Wait until chart definition is loaded and cube query has actually run
        if (isLoadingChartData || !chartData) {
            return;
        }
        const chartSettled = isTableChart ? !serverSideResult.isLoading : !isLoadingResultSet;
        if (!chartSettled) {
            return;
        }
        // Ensure we have results or an error before declaring settled
        if (!error && !resultSets) {
            return;
        }
        chartLoadLoggedRef.current = true;
        const loadDurationMs = Math.round(performance.now() - chartLoadStartRef.current);
        if (error) {
            onChartLoadStatus?.(chart.id, 'failed');
            logReportEvent?.(ReportAnalyticsEvents.CHART_LOAD_FAILED, {
                chart_id: chart.id,
                chart_type: chart.type,
                chart_title: chart.title,
                load_duration_ms: loadDurationMs,
                error_message: error.message,
            });
        }
        else {
            onChartLoadStatus?.(chart.id, 'loaded');
            logReportEvent?.(ReportAnalyticsEvents.CHART_LOAD_COMPLETED, {
                chart_id: chart.id,
                chart_type: chart.type,
                chart_title: chart.title,
                load_duration_ms: loadDurationMs,
            });
        }
    }, [isLoadingChartData, chartData, isLoadingResultSet, serverSideResult.isLoading, error, resultSets, isTableChart]);
    useEffect(() => {
        if (!onNonTableResultSetsChange) {
            return;
        }
        if (isTableChart) {
            onNonTableResultSetsChange(chart.id, null);
            return;
        }
        onNonTableResultSetsChange(chart.id, resultSets);
    }, [onNonTableResultSetsChange, isTableChart, chart.id, resultSets]);
    // Handler for pagination changes from TableChart
    const handlePaginationChange = (newPagination) => {
        logReportEvent?.(ReportAnalyticsEvents.TABLE_PAGE_CHANGED, {
            chart_id: chart.id,
            chart_title: chartData?.title ?? chart.title,
            page_index: newPagination.pageIndex,
            page_size: newPagination.pageSize,
        });
        setTablePageIndex(newPagination.pageIndex);
        setUserTablePageSize(newPagination.pageSize);
    };
    // Effect to handle export when data is ready
    useEffect(() => {
        if (shouldFetchForExport && !paginatedResult.isLoading && paginatedResult.resultSets) {
            // Data is ready, perform the export
            performExport(paginatedResult.resultSets);
            setShouldFetchForExport(false);
            setIsExporting(false);
        }
        else if (shouldFetchForExport && paginatedResult.error) {
            // Error fetching data
            const errorMessage = paginatedResult.error.message || 'An error occurred while fetching data for export.';
            logReportEvent?.(ReportAnalyticsEvents.EXPORT_FAILED, {
                export_scope: 'chart',
                chart_id: chart.id,
                chart_title: chartData?.title ?? chart.title,
                error_message: errorMessage,
            });
            toast.addNotification({
                type: 'error',
                title: errorMessage,
            });
            setShouldFetchForExport(false);
            setIsExporting(false);
        }
    }, [shouldFetchForExport, paginatedResult.isLoading, paginatedResult.resultSets, paginatedResult.error]);
    const performExport = (exportResultSets) => {
        try {
            const transformedData = getTransformedExportData({
                exportResultSets,
                chartData,
                isTableChart,
                locale,
                keepFormat: applyValueFormatting,
                showFullTimestamp,
            });
            const fileName = `${sanitizeFileName(chartData?.title || chart.title || 'chart')}_${getExportTimestamp()}.csv`;
            exportJsonToCsv(transformedData, fileName);
            logReportEvent?.(ReportAnalyticsEvents.EXPORT_COMPLETED, {
                export_scope: 'chart',
                chart_id: chart.id,
                chart_title: chartData?.title ?? chart.title,
            });
        }
        catch (error) {
            let errorMessage = 'An error occurred while exporting. Please try again.';
            if (error instanceof Error) {
                errorMessage = error.message;
            }
            else if (error && typeof error === 'object' && 'message' in error) {
                errorMessage = error.message;
            }
            logReportEvent?.(ReportAnalyticsEvents.EXPORT_FAILED, {
                export_scope: 'chart',
                chart_id: chart.id,
                chart_title: chartData?.title ?? chart.title,
                error_message: errorMessage,
            });
            toast.addNotification({
                type: 'error',
                title: errorMessage,
            });
        }
    };
    const handleExport = () => {
        logReportEvent?.(ReportAnalyticsEvents.EXPORT_STARTED, {
            export_scope: 'chart',
            chart_id: chart.id,
            chart_type: chart.type,
            chart_title: chartData?.title ?? chart.title,
        });
        if (isTableChart) {
            // For table charts, trigger fetch of all data then export
            setIsExporting(true);
            setShouldFetchForExport(true);
        }
        else {
            // For other charts, data is already loaded, export directly
            if (resultSets) {
                performExport(resultSets);
            }
        }
    };
    // Handle composite cards with dedicated component (after all hooks are called)
    if (chart.type === 'composite') {
        return (_jsx(CompositeCard, { chart: chart, chartQueryFilters: chartQueryFilters, chartPresetFilters: chartPresetFilters, setChartFilterModal: setChartFilterModal, filterParams: filterParams, studyId: studyId, isPublic: isPublic, forceFresh: forceDisablePreAggs, refreshNonce: refreshNonce }));
    }
    return (_jsx(ReportingCard, { 
        // No h-full here: the grid cell is a flex container, so the card fills it via align-items:
        // stretch. A percentage height resolved against the cell's min-h floored auto row track
        // collapses to content height in Safari 26 when the filter side panel reflows the page
        // width (SSP-4389).
        title: chartData ? (_jsx(ChartHeader, { chart: { ...chartData, id: chart.id, name: chart.name }, lastRefreshed: lastRefreshed, chartQueryFilters: chartQueryFilters, chartPresetFilters: chartPresetFilters, setChartFilterModal: setChartFilterModal, handleExport: handleExport, isLoading: isLoading || interactionsDisabled, isExporting: isExporting, logReportEvent: logReportEvent })) : (_jsx("div", { className: 'px-2', children: chart.title || chart.name })), children: isLoadingChartData ? (_jsx("div", { className: 'flex items-center justify-center size-full', children: _jsx(Spinner, { size: 'xl' }) })) : error ? (_jsxs("div", { children: ["Error -- ", error.message] })) : !chartData ? (_jsx("div", { children: "No data available" })) : (_jsx(ChartContent, { isLoading: isLoading || (!isTableChart && isLoadingResultSet), chartError: error, resultSets: resultSets || undefined, chartType: chartData.type, pivotConfig: chartData?.pivotConfig, style: chartData?.style, setSortingConfig: (config) => {
                logReportEvent?.(ReportAnalyticsEvents.TABLE_SORT_CHANGED, {
                    chart_id: chart.id,
                    chart_title: chartData?.title ?? chart.title,
                    sort_fields: Object.keys(config).join(','),
                });
                setSortingConfig(config);
            }, defaultSort: configuredDefaultSort, tooltipConfig: chartData?.tooltipConfig, bubbleConfig: chartData?.bubbleConfig, showAnnotations: chartData?.showAnnotations, disablePreAggs: chartData?.disablePreAggs, roleBasedVisibility: chartData?.roleBasedVisibility, palette: chartData?.palette, xTitle: chartData?.xTitle, yTitle: chartData?.yTitle, builderChartType: chartData?.builderChartType, showLabels: chartData?.showLabels, showLegend: chartData?.showLegend, numberValues: chartData?.numberValues, labelOverrides: chartData?.labelOverrides, xAxis: chartData?.xAxis, yAxis: chartData?.yAxis, splitBy: chartData?.splitBy, dotSizeMapping: chartData?.dotSizeMapping, dotSizeField: chartData?.dotSizeField, dotSizeConstant: chartData?.dotSizeConstant, normalizeBubbleSize: chartData?.normalizeBubbleSize, bubbleSizeMinOverride: chartData?.bubbleSizeMinOverride, bubbleSizeMaxOverride: chartData?.bubbleSizeMaxOverride, serverSidePagination: isTableChart
                ? {
                    total: totalRows,
                    paginationState,
                    onPaginationChange: handlePaginationChange,
                }
                : undefined, showFullTimestamp: showFullTimestamp })) }, chart.id));
};
